import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];
  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  handleInput = debounce(() => {
    this.#renderResults();
  }, 250);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';
    this.#renderResults();
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  async #renderResults() {
    const searchInput = this.refs.searchPageInput;
    const searchTerm = searchInput.value.trim();

    const resultsList = document.querySelector('results-list');
    const sectionId = resultsList?.getAttribute('section-id');
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    if (searchTerm.length > 0) {
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('type', 'product');
    } else {
      url.searchParams.delete('q');
      url.searchParams.delete('type');
      url.searchParams.delete('sort_by');
    }

    history.replaceState({}, '', url.toString());

    const abortController = this.#createAbortController();
    await sectionRenderer.renderSection(sectionId, { cache: false, url });
    if (abortController.signal.aborted) return;
  }
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
